import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { SpecificationJurisdiction } from '../enums';

export interface SpecificationStorageData {
    /** 受理局 */
    jurisdiction: SpecificationJurisdiction;
    /** 交底书 */
    inventionDisclosure: string;
    /** 权利要求 */
    claims: string;
}

const STORAGE_KEY = 'specification-input-data';

/**
 * Specification 存储服务
 * 用于在 domain 层处理从首页传递过来的输入数据
 */
export interface SpecificationStorageService {
    getStorageData(): SpecificationStorageData | null;
    setStorageData(data: SpecificationStorageData): void;
    hasStorageData(): boolean;
    clearStorageData(): void;
}

@injectable()
export class SpecificationStorageServiceImpl implements SpecificationStorageService {
    /**
     * 读取存储的输入数据
     */
    getStorageData(): SpecificationStorageData | null {
        try {
            const data = localStorage.getItem(STORAGE_KEY);
            if (!data) {
                return null;
            }
            return JSON.parse(data) as SpecificationStorageData;
        }
        catch (error) {
            console.error('Failed to parse specification storage data:', error);
            return null;
        }
    }

    /**
     * 设置存储的输入数据
     */
    setStorageData(data: SpecificationStorageData): void {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        }
        catch (error) {
            console.error('Failed to set specification storage data:', error);
        }
    }

    /**
     * 检查是否有存储的数据
     */
    hasStorageData(): boolean {
        const data = this.getStorageData();
        return data !== null
            && data.inventionDisclosure !== ''
            && data.claims !== '';
    }

    /**
     * 清空存储的数据
     */
    clearStorageData(): void {
        localStorage.removeItem(STORAGE_KEY);
    }
}

export const SpecificationStorageService = Symbol.for('SpecificationStorageService');
